/**
 * Platform Reviews Tab
 * Admin interface for moderating tool reviews
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/utils/cn";
import { apiGet, apiPatch } from "@/services/api";
import { StarRating } from "@/components/tools";
import type {
  ApiAdminReviewListResponse,
  ToolReviewWithUser,
  ReviewStatus,
} from "@/types/tools";
import { transformAdminReviewListResponse } from "@/types/tools";

// Query keys
const reviewsKeys = {
  all: ["admin", "reviews"] as const,
  list: (status: ReviewStatus, page: number) =>
    [...reviewsKeys.all, { status, page }] as const,
};

// API functions
async function getAdminReviews(
  status: ReviewStatus,
  page: number,
  pageSize: number,
) {
  const response = await apiGet<ApiAdminReviewListResponse>(
    "/platform-admin/tools/reviews",
    { status, page, page_size: pageSize },
  );
  return transformAdminReviewListResponse(response);
}

async function moderateReview(
  reviewId: string,
  action: "approve" | "reject",
  rejectionReason?: string,
) {
  await apiPatch(`/platform-admin/tools/reviews/${reviewId}`, {
    action,
    rejection_reason: rejectionReason,
  });
}

export function PlatformReviewsTab() {
  const [statusFilter, setStatusFilter] = useState<ReviewStatus>("pending");
  const [page, setPage] = useState(1);
  const [selectedReview, setSelectedReview] =
    useState<ToolReviewWithUser | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);

  const queryClient = useQueryClient();

  // Fetch reviews
  const { data, isLoading, error } = useQuery({
    queryKey: reviewsKeys.list(statusFilter, page),
    queryFn: () => getAdminReviews(statusFilter, page, 20),
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: (reviewId: string) => moderateReview(reviewId, "approve"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewsKeys.all });
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: ({ reviewId, reason }: { reviewId: string; reason?: string }) =>
      moderateReview(reviewId, "reject", reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewsKeys.all });
      setShowRejectModal(false);
      setSelectedReview(null);
      setRejectionReason("");
    },
  });

  const handleApprove = (review: ToolReviewWithUser) => {
    approveMutation.mutate(review.id);
  };

  const handleRejectClick = (review: ToolReviewWithUser) => {
    setSelectedReview(review);
    setRejectionReason("");
    setShowRejectModal(true);
  };

  const handleRejectConfirm = () => {
    if (selectedReview) {
      rejectMutation.mutate({
        reviewId: selectedReview.id,
        reason: rejectionReason || undefined,
      });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-10 w-64 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 dark:text-red-400">
          Failed to load reviews. Please try again.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Review Moderation
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Approve or reject user-submitted tool reviews
          </p>
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-2">
          {(["pending", "approved", "rejected"] as const).map((status) => (
            <button
              key={status}
              onClick={() => {
                setStatusFilter(status);
                setPage(1);
              }}
              className={cn(
                "px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                statusFilter === status
                  ? "bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600",
              )}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
          <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">
            {statusFilter === "pending" ? (data?.total ?? 0) : "-"}
          </div>
          <div className="text-sm text-yellow-600 dark:text-yellow-400">
            Pending Reviews
          </div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
          <div className="text-2xl font-bold text-green-700 dark:text-green-300">
            {statusFilter === "approved" ? (data?.total ?? 0) : "-"}
          </div>
          <div className="text-sm text-green-600 dark:text-green-400">
            Approved Reviews
          </div>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
          <div className="text-2xl font-bold text-red-700 dark:text-red-300">
            {statusFilter === "rejected" ? (data?.total ?? 0) : "-"}
          </div>
          <div className="text-sm text-red-600 dark:text-red-400">
            Rejected Reviews
          </div>
        </div>
      </div>

      {/* Reviews Table */}
      {data && data.reviews.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Review
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Rating
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Submitted
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {data.reviews.map((review) => (
                <tr
                  key={review.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  <td className="px-6 py-4">
                    <div className="max-w-xs">
                      {review.reviewTitle && (
                        <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                          {review.reviewTitle}
                        </div>
                      )}
                      {review.reviewText && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                          {review.reviewText}
                        </div>
                      )}
                      {!review.reviewTitle && !review.reviewText && (
                        <span className="text-gray-400 italic">
                          Rating only
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {review.userName || "Anonymous"}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">
                        {review.userEmail}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <StarRating value={review.rating} size="sm" />
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {formatDistanceToNow(new Date(review.createdAt), {
                      addSuffix: true,
                    })}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {statusFilter === "pending" && (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleApprove(review)}
                          disabled={approveMutation.isPending}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleRejectClick(review)}
                          disabled={rejectMutation.isPending}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                    {statusFilter === "approved" && (
                      <span className="text-green-600 dark:text-green-400 text-sm">
                        Approved
                      </span>
                    )}
                    {statusFilter === "rejected" && (
                      <div>
                        <span className="text-red-600 dark:text-red-400 text-sm">
                          Rejected
                        </span>
                        {review.rejectionReason && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Reason: {review.rejectionReason}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {data.totalPages > 1 && (
            <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Page {page} of {data.totalPages} ({data.total} reviews)
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                  className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= data.totalPages}
                  className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-gray-500 dark:text-gray-400">
            No {statusFilter} reviews found.
          </p>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowRejectModal(false)}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Reject Review
            </h3>

            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <StarRating value={selectedReview.rating} size="sm" />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  by {selectedReview.userName || "Anonymous"}
                </span>
              </div>
              {selectedReview.reviewTitle && (
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {selectedReview.reviewTitle}
                </div>
              )}
              {selectedReview.reviewText && (
                <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  {selectedReview.reviewText}
                </div>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Rejection Reason (optional)
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why this review is being rejected..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                This reason will be visible to the user who submitted the
                review.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={rejectMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
              >
                {rejectMutation.isPending ? "Rejecting..." : "Reject Review"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
